import { getAnalyticsUrl, getManagerUrl } from "@/config";
import { ITableRowData } from "@/shared/components/normal-table/normal-table";
import { RenderFields } from "@/shared/utils/render-fields";
import { activeOptions } from "@/shared/utils/static-values";
import { IDatamodelSettings } from "./datamodel-page";
import { IFieldConfigDefinition } from "@/shared/definitions/field";
import { getAllData } from "@/shared/utils/api";
import useHttp from "@/shared/hooks/use-http";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "@/shared/context/auth-context";
import { validateLoadingStatusNew } from "@/shared/utils/utils";
import Spinner from "@/shared/components/spinner/spinner";
import Collapsible from "@/manager/components/collapsible/collapsible";

interface ISettingsTabProps {
  datamodelType: 'event' | 'entity' | 'alertcase';
  datamodelSettings: IDatamodelSettings;
  datamodelFields: ITableRowData[];
  handleSettingsChange: (
    settingsModalData: IDatamodelSettings
  ) => void;
}

const SettingsTab = ({datamodelType, datamodelSettings, datamodelFields, handleSettingsChange} : ISettingsTabProps) => {
  const authCtx = useContext(AuthContext);
  const managerEnabled = getManagerUrl() !== "";
  const analyticsEnabled = getAnalyticsUrl() !== "";

  const [alertCaseFields, setAlertCaseFields] = useState<ITableRowData[]>([]);

  const {
    sendRequest: getAlertCases,
    status: getAlertCasesStatus,
    data: getAlertCasesData,
  } = useHttp(getAllData, false, authCtx.token);

  const {
    sendRequest: getAlertCase,
    status: getAlertCaseStatus,
    data: getAlertCaseData,
    error: getAlertCaseError,
  } = useHttp(getAllData, false, authCtx.token, "alertcase");

  const {
    sendRequest: getLists,
    status: getListsStatus,
    data: getListsData,
  } = useHttp(getAllData, false, authCtx.token);

  useEffect(() => {
    if (datamodelType === "event") {
      getAlertCases("datamodel/alertcases?archived=false");
      getLists("datamodel/enumerations");
    }
  }, []);

  useEffect(() => {
    if (datamodelSettings.alertcase_id) {
      getAlertCase(`datamodel/alertcase/${datamodelSettings.alertcase_id}`);
    }
  }, [datamodelSettings.alertcase_id]);

  useEffect(() => {
    if (getAlertCaseStatus === "completed" && !getAlertCaseError && getAlertCaseData) {
      setAlertCaseFields(getAlertCaseData.rowData || []);
    }
  }, [getAlertCaseError, getAlertCaseData, getAlertCaseStatus]);

  let generalSettingFields: IFieldConfigDefinition[] = [
    {
      key: "datetime_format",
      displayName: "Datetime format",
      dataType: {
        type: "text",
      },
      controlled: true,
      extraToolTipInfo:
        "The datetime format used by this field `%Y-%m-%d %H:%M:%S` (e.g., 2024-07-05 14:30:00)",
    },
    {
      key: "csv_separator",
      displayName: "Separator",
      dataType: {
        type: "text",
      },
      controlled: true,
    },
  ];
  let analyticsSettingsFields: IFieldConfigDefinition[] = [];
  let managerSettingsFields: IFieldConfigDefinition[] = [];

  if (datamodelType === "event") {
    generalSettingFields.push({
      key: "alertcase",
      displayName: "AlertCase",
      dataType: {
        type: "reference",
        values: getAlertCasesData ? getAlertCasesData.rows : [],
      },
      onFocusEmpty: true,
      required: false,
      extraToolTipInfo:
        "AlertCase settings require at least 1 field with a primary key",
    });

    if (analyticsEnabled) {
      analyticsSettingsFields.push({
        key: "enable_scoring",
        displayName: "Enable scoring",
        dataType: {
          type: "enumeration",
          values: activeOptions,
        },
        noEmptyDefault: true,
      });

      if (
        datamodelSettings.alertcase &&
        datamodelSettings.alertcase_id
      ) {
        const allowedDatatypes = ["boolean", "text", "integer"];
        const fieldOptions = alertCaseFields.filter(row => allowedDatatypes.includes(row.field_datatype)).map((col) => ({
          id: col.id,
          name: col.input_field_name,
        }));
        analyticsSettingsFields.push(
          {
            key: "alertcase_label_field",
            displayName: "Label field",
            dataType: {
              type: "reference",
              values: fieldOptions,
            },
            onFocusEmpty: true,
          },
          {
            key: "alertcase_field",
            displayName: "AlertCase field",
            dataType: {
              type: "reference",
              values: fieldOptions
            },
            onFocusEmpty: true,
          }
        );

        const isLabelFieldNotBoolean = datamodelFields.find(row => row.id === datamodelSettings.alertcase_label_field_id)?.field_datatype !== "boolean"
        if (datamodelSettings.alertcase_label_field_id && isLabelFieldNotBoolean) {
          analyticsSettingsFields.push({
            key: "label_list",
            displayName: "List label positive",
            dataType: {
              type: "reference",
              values: getListsData ? getListsData.rows : [],
            },
            onFocusEmpty: true
          });
        }

        const isAlertFieldNotBoolean = datamodelFields.find(row => row.id === datamodelSettings.alertcase_field_id)?.field_datatype !== "boolean"
        if (datamodelSettings.alertcase_field_id && isAlertFieldNotBoolean) {
          analyticsSettingsFields.push({
            key: "alertcase_list",
            displayName: "List alertcase positive",
            dataType: {
              type: "reference",
              values: getListsData ? getListsData.rows : [],
            },
            onFocusEmpty: true
          });
        }
      }
    }
    if (managerEnabled && datamodelSettings.alertcase_id) {
      managerSettingsFields.push(
      {
        key: "primary_datetime_column",
        displayName: "Primary datetime column",
        dataType: {
          type: "reference",
          values: datamodelFields.filter(row => row.field_datatype === "datetime").map((col) => ({
            id: col.id,
            name: col.input_field_name,
          })),
        },
        extraToolTipInfo:
          "The main datetime column used for event consolidation",
      });
      managerSettingsFields.push({
        key: "process_type_column",
        displayName: "Process type column",
        dataType: {
          type: "reference",
          values: datamodelFields.filter(row => row.field_datatype === "text").map((col) => ({
            id: col.id,
            name: col.input_field_name,
          })),
        },
        extraToolTipInfo: "Column that identifies the process type",
      });
    }
  }

  const onSettingsChange = (
    targetVal: string,
    inputKey: string,
    id: string
  ) => {
    if (
      inputKey === "alertcase" ||
      inputKey === "alertcase_field" ||
      inputKey === "alertcase_label_field" ||
      inputKey === "alertcase_list" ||
      inputKey === "label_list" ||
      inputKey === "primary_datetime_column" ||
      inputKey === "process_type_column"
    ) {
      handleSettingsChange({
        ...datamodelSettings,
        [inputKey]: targetVal,
        [`${inputKey}_id`]: id,
      });
    } else {
      handleSettingsChange({
        ...datamodelSettings,
        [inputKey]: targetVal,
      });
    }
  };

  return (
    <div className="settingsTab">
      {
        validateLoadingStatusNew([getAlertCasesStatus, getAlertCaseStatus, getListsStatus]) && <Spinner/>
      }
      <Collapsible title="General Settings">
        <RenderFields
          fields={generalSettingFields}
          data={{ ...datamodelSettings, id: "" }}
          handleOnChange={onSettingsChange}
        />
      </Collapsible>
      {analyticsSettingsFields.length > 0 && (
        <Collapsible title="Analytics Configuration">
          <RenderFields
            fields={analyticsSettingsFields}
            data={{ ...datamodelSettings, id: "" }}
            handleOnChange={onSettingsChange}
          />
        </Collapsible>
      )}
      {managerSettingsFields.length > 0 && (
        <Collapsible title="Manager Configuration">
          <RenderFields
            fields={managerSettingsFields}
            data={{ ...datamodelSettings, id: "" }}
            handleOnChange={onSettingsChange}
          />
        </Collapsible>
      )}
    </div>
  );
};

export default SettingsTab